import type { CMSAssetType, CMSAssetVerificationParams } from '../types/index';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@workramp/ui';
import { getCSRFToken } from '~/utils';
import { I18n } from '~/common/explicit-exports';
import { GET_LIST_ASSET_KEY, CMS_ASSET_DETAILS_KEY } from '../constants';

const LINK_UPDATED = I18n.t('js.admin.cms.link_updated');

export type UpdateLinkParams = {
  name: string;
  description: string;
  cardBanner?: File;
  id: string;
  url: string;
  allowExternalSharing?: boolean;
  tags?: string[];
  verification?: CMSAssetVerificationParams;
};

const updateLink = async (updateLinkParams: UpdateLinkParams): Promise<Response> => {
  const data = new FormData();
  // Note: cardBanner will be a valid File object here when user uploads a new file, it will
  // be null when user removes the file, and it will be undefined when user does not change
  // the file (hence don't need to add to our request).
  if (updateLinkParams.cardBanner) {
    data.append('cardBanner', updateLinkParams.cardBanner);
  } else if (updateLinkParams.cardBanner === null) {
    // To remove the file we need to send an empty string to backend.
    data.append('cardBanner', '');
  }

  if (typeof updateLinkParams.description === 'string') {
    data.append('description', updateLinkParams.description);
  }

  if (updateLinkParams.name) {
    data.append('name', updateLinkParams.name);
  }

  if (updateLinkParams.url) {
    data.append('url', updateLinkParams.url);
  }

  if (updateLinkParams.tags && updateLinkParams.tags.length > 0) {
    updateLinkParams.tags.forEach((tag) => {
      data.append('tags[]', tag);
    });
  } else {
    // Ensure the `tags[]` field is still included in the request as an empty array or empty string
    data.append('tags[]', '');
  }

  if (updateLinkParams.allowExternalSharing !== null) {
    data.append('allowExternalSharing', updateLinkParams.allowExternalSharing.toString());
  }

  if (updateLinkParams.verification) {
    data.append('verification[reviewerUserId]', updateLinkParams.verification.reviewerUserId);
    data.append('verification[interval]', updateLinkParams.verification.interval);
    data.append(
      'verification[isVerifiableByCmsAdmins]',
      updateLinkParams.verification.isVerifiableByCmsAdmins?.toString(),
    );
  }

  const url = `/api/cms/link/${updateLinkParams.id}`;
  const response = await fetch(url, {
    method: 'PUT',
    body: data,
    headers: {
      contentType: 'multipart/form-data',
      'X-CSRF-TOKEN': getCSRFToken(),
    },
  });

  // Because fetch resolves rather than treat a 400 error as a rejection, we need to manually check for errors and return a rejected promise.
  if (!response.ok) {
    const responseError = await response.json();
    return Promise.reject(new Error(responseError.message));
  }
  return response;
};
export const useUpdateLink = (assetId: string, assetType: CMSAssetType) => {
  const queryClient = useQueryClient();
  const { open: openToast } = useToast();

  return useMutation(updateLink, {
    onSuccess: async () => {
      openToast({
        children: `${LINK_UPDATED}`,
      });
      await queryClient.invalidateQueries([CMS_ASSET_DETAILS_KEY, assetType.toUpperCase(), assetId]);
      await queryClient.invalidateQueries([GET_LIST_ASSET_KEY]);
    },
    onError: (err: Error) => {
      openToast({
        children: err.message,
      });
    },
  });
};
